import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from '@/components/ui/command';
import { Check, ChevronsUpDown, X, ArrowUpDown } from 'lucide-react';
import { useBrand } from '@/config/brands';
import { stateAbbreviations } from '@/components/USAMapNavigator';
import { scenarioMarkerColor } from '@/lib/grades';
import { useLiveDb } from '@/lib/offlineData';
import { loadScenarioCoverageFor, getCoverageForScenario, getHospitalInfo } from '@/lib/scenarioCoverageStore';
import { blendCoverageRow } from '@/lib/priceSanity';
import { CoverageProgressLoader } from '@/lib/CoverageProgressLoader';

interface ScenarioCoverageRow {
  hospital_id: string;
  total_hospital_price: number;
  total_national_price: number;
  avg_markup_percentage: number;
  coverage_percentage: number;
  matched_codes: number;
  total_codes: number;
  hospitals: {
    name: string;
    city: string;
    state: string;
  };
}

interface ScenarioRankingsPanelProps {
  scenarioId: string;
  scenarioName: string;
  nationalAvgPrice: number;
  onHospitalClick: (hospitalId: string) => void;
  onClear: () => void;
}

export function ScenarioRankingsPanel({
  scenarioId,
  scenarioName,
  nationalAvgPrice,
  onHospitalClick,
  onClear,
}: ScenarioRankingsPanelProps) {
  const brand = useBrand();
  const isAhp = brand.key === 'ahp';
  const accentColor = isAhp ? 'text-[#C5A55A]' : 'text-matrix-green';

  const liveDb = useLiveDb();
  const [stateFilter, setStateFilter] = useState('USA');
  const [statePopoverOpen, setStatePopoverOpen] = useState(false);
  const [sortAscending, setSortAscending] = useState(true); // cheapest first by default
  const [displayCount, setDisplayCount] = useState(30);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { data: rankings = [], isLoading } = useQuery({
    queryKey: ['scenario-map-rankings', scenarioId, liveDb],
    queryFn: async () => {
      // Try static cache first (unless ?db=live)
      if (!liveDb) {
        const loaded = await loadScenarioCoverageFor(scenarioId);
        if (loaded) {
          const rows = getCoverageForScenario(scenarioId);
          if (rows.length > 0) {
            return rows.map(row => {
              const hospital = getHospitalInfo(row.h);
              return {
                hospital_id: row.h,
                total_hospital_price: row.hp,
                total_national_price: row.np,
                avg_markup_percentage: row.mp,
                coverage_percentage: row.cp,
                matched_codes: row.mc,
                total_codes: row.tc,
                hospitals: {
                  name: hospital?.n || 'Unknown',
                  city: hospital?.c || '',
                  state: hospital?.s || '',
                },
              } as ScenarioCoverageRow;
            });
          }
        }
      }

      // Supabase fallback
      const { data, error } = await supabase
        .from('scenario_hospital_coverage')
        .select('hospital_id, total_hospital_price, total_national_price, avg_markup_percentage, coverage_percentage, matched_codes, total_codes, hospitals!inner(name, city, state)')
        .eq('scenario_id', scenarioId)
        .eq('cornerstone_pass', true)
        .gte('coverage_percentage', 50)
        .gt('total_hospital_price', 0)
        .order('total_hospital_price', { ascending: true });
      if (error) { console.error('Scenario rankings error:', error); return []; }
      return (data || []) as unknown as ScenarioCoverageRow[];
    },
    enabled: !!scenarioId,
  });

  // Blended price: hospital prices for matched codes + national avg for unmatched
  // codes — via shared sanitizer (returns null for garbage/unpriceable rows)
  const getBlended = (r: ScenarioCoverageRow) =>
    blendCoverageRow({
      hp: r.total_hospital_price,
      np: r.total_national_price,
      mc: r.matched_codes,
      tc: r.total_codes,
      nationalCost: nationalAvgPrice,
    });

  // Filter by state, dropping garbage rows
  let filtered = rankings.filter(r => getBlended(r) !== null);
  if (stateFilter !== 'USA') {
    filtered = filtered.filter(r => r.hospitals.state === stateFilter);
  }

  const getBlendedPrice = (r: ScenarioCoverageRow) =>
    getBlended(r)?.blendedPrice ?? r.total_hospital_price;

  const getBlendedMarkup = (r: ScenarioCoverageRow) =>
    getBlended(r)?.blendedMarkup ?? r.avg_markup_percentage;

  // Sort by blended price
  const sorted = [...filtered].sort((a, b) => {
    const diff = getBlendedPrice(a) - getBlendedPrice(b);
    return sortAscending ? diff : -diff;
  });

  // Reset display count on filter/sort change
  useEffect(() => {
    setDisplayCount(30);
  }, [stateFilter, sortAscending]);

  // Infinite scroll
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    if (scrollTop + clientHeight >= scrollHeight - 100 && displayCount < sorted.length) {
      setDisplayCount(prev => Math.min(prev + 30, sorted.length));
    }
  }, [displayCount, sorted.length]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const getStateName = (stateCode: string) => {
    if (stateCode === 'USA') return 'All Hospitals';
    const entry = Object.entries(stateAbbreviations).find(([_, code]) => code === stateCode);
    return entry ? entry[0] : stateCode;
  };

  const getRowColor = (markup: number) => {
    if (markup > 1000) return 'bg-red-950/80 hover:bg-red-950';
    if (markup > 500) return 'bg-red-900/60 hover:bg-red-900/80';
    if (markup > 300) return 'bg-orange-900/40 hover:bg-orange-900/60';
    if (markup > 200) return 'bg-yellow-900/30 hover:bg-yellow-900/50';
    if (markup > 100) return 'bg-yellow-800/20 hover:bg-yellow-800/40';
    if (markup > 50) return 'bg-orange-800/20 hover:bg-orange-800/40';
    if (markup > 0) return 'bg-red-800/20 hover:bg-red-800/40';
    return 'bg-green-900/20 hover:bg-green-900/40';
  };

  const getMarkupColor = (markup: number) => {
    return scenarioMarkerColor(markup);
  };

  return (
    <div className="space-y-4">
      <div className={`${isAhp ? 'bg-[#001c48]/60 border-[#C5A55A]/20' : 'bg-gradient-terminal border-border'} border rounded-lg p-4`}>
        <div className="flex items-center justify-between mb-1">
          <h2
            className="text-lg font-bold truncate flex-1 mr-2"
            style={isAhp ? { fontFamily: "'Instrument Serif', Georgia, serif", color: '#FAF9F6' } : { color: 'var(--foreground)' }}
          >
            {scenarioName}
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClear}
            className="shrink-0 text-xs gap-1 text-muted-foreground hover:text-foreground"
          >
            <X className="w-3.5 h-3.5" />
            Clear
          </Button>
        </div>
        <p className={`text-xs mb-3 ${isAhp ? 'text-white/60' : 'text-muted-foreground'}`}>
          Scenario pricing — hospitals ranked by estimated total cost
        </p>

        {/* State filter */}
        <div className="mb-3">
          <Popover open={statePopoverOpen} onOpenChange={setStatePopoverOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" role="combobox" className="w-full justify-between text-sm">
                {stateFilter === 'USA' ? 'All States (USA)' : getStateName(stateFilter)}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-full p-0" align="start">
              <Command>
                <CommandInput placeholder="Type state name..." />
                <CommandEmpty>No state found.</CommandEmpty>
                <CommandGroup className="max-h-[300px] overflow-auto">
                  <CommandItem value="USA" onSelect={() => { setStateFilter('USA'); setStatePopoverOpen(false); }}>
                    <Check className={`mr-2 h-4 w-4 ${stateFilter === 'USA' ? 'opacity-100' : 'opacity-0'}`} />
                    All States (USA)
                  </CommandItem>
                  {Object.entries(stateAbbreviations).map(([name, code]) => (
                    <CommandItem key={code} value={name} onSelect={() => { setStateFilter(code); setStatePopoverOpen(false); }}>
                      <Check className={`mr-2 h-4 w-4 ${stateFilter === code ? 'opacity-100' : 'opacity-0'}`} />
                      {name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {/* Sort toggle */}
        <button
          onClick={() => setSortAscending(!sortAscending)}
          className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full transition-colors mb-2 ${
            isAhp ? 'bg-[#C5A55A]/20 text-[#C5A55A] hover:bg-[#C5A55A]/30' : 'bg-matrix-green/10 text-matrix-green hover:bg-matrix-green/20'
          }`}
        >
          <ArrowUpDown className="w-3 h-3" />
          {sortAscending ? 'Cheapest First' : 'Most Expensive First'}
        </button>

        {isLoading ? (
          <CoverageProgressLoader />
        ) : (
          <p className={`text-sm ${isAhp ? 'text-white/70' : 'text-muted-foreground'}`}>
            {sorted.length} hospitals with ≥50% code coverage
          </p>
        )}
      </div>

      {/* Rankings table */}
      <div ref={scrollContainerRef} className="overflow-auto" style={{ maxHeight: 'calc(100vh - 340px)' }}>
        <table className="w-full text-sm border-collapse">
          <thead className={`sticky top-0 backdrop-blur-sm border-b z-10 ${isAhp ? 'bg-[#001440]/95 border-[#C5A55A]/20' : 'bg-background/95 border-border'}`}>
            <tr>
              <th className={`text-left p-2 text-xs font-semibold ${isAhp ? 'text-white' : 'text-muted-foreground'}`}>#</th>
              <th className={`text-left p-2 text-xs font-semibold ${isAhp ? 'text-white' : 'text-muted-foreground'}`}>Hospital</th>
              <th className={`text-right p-2 text-xs font-semibold ${isAhp ? 'text-white' : 'text-muted-foreground'}`}>Est. Cost</th>
              <th className={`text-right p-2 text-xs font-semibold ${isAhp ? 'text-white' : 'text-muted-foreground'}`}>Markup</th>
            </tr>
          </thead>
          <tbody>
            {sorted.slice(0, displayCount).map((row, index) => {
              const blendedPrice = getBlendedPrice(row);
              const blendedMarkup = getBlendedMarkup(row);
              const isPartial = row.matched_codes < row.total_codes;
              return (
              <tr
                key={row.hospital_id}
                onClick={() => onHospitalClick(row.hospital_id)}
                className={`border-b ${isAhp ? 'border-white/10' : 'border-border/30'} cursor-pointer transition-all ${getRowColor(blendedMarkup)}`}
              >
                <td className={`p-2 font-bold font-mono ${isAhp ? 'text-white/90' : 'text-white'}`}>
                  {index + 1}
                </td>
                <td className="p-2">
                  <div className={`font-medium text-xs truncate max-w-[180px] ${isAhp ? 'text-white' : 'text-foreground'}`}>
                    {row.hospitals.name}
                  </div>
                  <div className={`text-[11px] ${isAhp ? 'text-white/50' : 'text-muted-foreground'}`}>
                    {row.hospitals.city}, {row.hospitals.state}
                    <span className="ml-1 opacity-60">{row.matched_codes}/{row.total_codes}</span>
                  </div>
                </td>
                <td className={`p-2 text-right font-semibold text-sm ${isAhp ? 'text-white' : 'font-mono text-white'}`}>
                  {isPartial ? '~' : ''}${blendedPrice.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </td>
                <td className="p-2 text-right">
                  <span
                    className="text-xs font-semibold px-1.5 py-0.5 rounded-full"
                    style={{
                      color: getMarkupColor(blendedMarkup),
                      backgroundColor: `${getMarkupColor(blendedMarkup)}20`,
                    }}
                  >
                    {blendedMarkup > 0 ? '+' : ''}{blendedMarkup.toFixed(0)}%
                  </span>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
        {sorted.length > displayCount && (
          <div className={`text-center py-4 text-sm ${isAhp ? 'text-white/50' : 'text-muted-foreground'}`}>
            Scroll down to load more... ({sorted.length - displayCount} remaining)
          </div>
        )}
      </div>
    </div>
  );
}
